import {AbstractMainPlugin} from '../../AbstractMainPlugin';
import {Item} from '@src/app/models/Item';
import {ItemManagerService} from '@src/app/services/itemManager/item-manager.service';
import {appInjector} from '@src/app/classes/appInjector';
import {Subject} from 'rxjs';
import {debounceTime, filter, skipUntil, skipWhile, take, takeUntil, tap} from 'rxjs/operators';
import * as turf from '@turf/turf';
import {GroupService} from '@src/app/services/groupService/group-service.service';
import {RoomLayoutService} from '@src/app/services/roomLayoutService/room-layout-service.service';
import {isPresetEnabled} from '@src/configs/globalVariables';

interface itemSetUpInput {
  roomLayoutId: number,
  setUpId: number
}

export class SetUpPlugin extends AbstractMainPlugin {

  private im: ItemManagerService;
  private gs: GroupService;
  private rls: RoomLayoutService;
  private destroy$ = new Subject();
  private isChildInit$ = new Subject();
  private roomLayoutItems = {};
  private selectedSetUp = null;
  private _showItemsInSetUpDebounce = new Subject();
  private parent: Item;

  constructor(item: Item) {
    super(item);
    this.im = appInjector.get(ItemManagerService);
    this.gs = appInjector.get(GroupService);
    this.rls = appInjector.get(RoomLayoutService);

    if (isPresetEnabled) {

      if (this.item.config.getTool().is_preset) {
        this.initParentListeners();
      }
      if (this.item.config.getInputProp(Item.SET_UP_ITEM_INPUT, 'value')) {
        this.gs.itemsLoaded$.pipe(debounceTime(200)).subscribe((status) => {
          if (status) {
            this.initChildListeners();
          }
        });
      }

      this.item.getObservable('INIT_SET_UP_ITEM').pipe(takeUntil(this.isChildInit$)).subscribe(() => {
        this.initChildListeners(true);
      });
    }
  }

  /**
   * return selected setUp in room layout
   * @private
   */
  private isParentActive() {
    return this.item.config.getInputProp(Item.SET_UP_AREA_INPUT, 'selected');
  }

  private initParentListeners() {
    this.parent = null;
    if (this.isParentActive()){
      this.selectedSetUp = this.isParentActive();

      //remove set up item input if it's a set up area
      if (this.item.config.getInputProp(Item.SET_UP_ITEM_INPUT, 'value')){
        this.item.config.removeInput(Item.SET_UP_ITEM_INPUT);
      }
    }
    this.im.itemAdded$.pipe(skipWhile(val => !this.gs.itemsLoadedVal)).pipe(takeUntil(this.destroy$), filter(val => this.isParentActive() && val.config.getId() != this.item.config.getId())).subscribe((_item: Item) => {
      this.processBindingToRoomLayout(_item);
    });
    this.item.getObservable('addItemToRoomLayout').pipe(skipWhile(val => !this.gs.itemsLoadedVal)).pipe(takeUntil(this.destroy$), filter((val) => this.isParentActive())).subscribe((childItem: Item) => {
      let selectedSetUpId = childItem.config.getInputProp(Item.SET_UP_ITEM_INPUT, 'value').setUpId;
      if (this.item.config.getConcreteLayerId() != childItem.config.getConcreteLayerId()){
        return;
      }
      this.addItemToRoomLayout(selectedSetUpId, childItem);
      this._showItemsInSetUpDebounce.next(true);
    });
    this.rls.parentItemDragEnd$.pipe(takeUntil(this.destroy$), filter(val => this.isParentActive())).subscribe((_item: Item) => {
      this.processBindingToRoomLayout(_item);
    });
    this.item.getObservable('selectedSetUpChanged').pipe(takeUntil(this.destroy$)).subscribe((setUpId)=>{
      this.selectedSetUp = setUpId;
      if (setUpId!=null){
        this.showItemsInSetUp(setUpId);
      }
    });

    this.item.getObservable('removeSetUp').pipe(takeUntil(this.destroy$)).subscribe((config:{removedSetUpId, newSetUpId})=>{
      this.selectedSetUp = config.newSetUpId;
      let items = this.roomLayoutItems[config.removedSetUpId];
      if (items){
        items.forEach((i:Item)=>{
          i.removeObject();
        })
      }
      delete this.roomLayoutItems[config.removedSetUpId];
      this.showItemsInSetUp(config.newSetUpId);
    });

    this._showItemsInSetUpDebounce.pipe(takeUntil(this.destroy$), filter(val => this.isParentActive())).pipe(debounceTime(500)).pipe(take(1), filter(val => this.isParentActive())).subscribe(() => {
      this.showItemsInSetUp(this.isParentActive());
    });

    this.item.getObservable('editStop').pipe(debounceTime(500)).subscribe(()=>{
      // let selectedRoomLayoutItems = (Array.isArray(this.roomLayoutItems[this.selectedSetUp])) ? this.roomLayoutItems[this.selectedSetUp] : [];
      this.im.getAllItems().filter(i => i.getId() != this.item.getId() && i.getSubjectValue(Item.SUBJECT_VISIBILITY) && !i.config.getInputByName(Item.SET_UP_AREA_INPUT)).forEach((i:Item)=>{
          this.processBindingToRoomLayout(i);
      });
    })
  }

  private showItemsInSetUp(setUpId){
    for (let i in this.roomLayoutItems){
      let items = this.roomLayoutItems[i];
      if (i == setUpId){
        this.setItemsVisibility(items, true);
      }
      else{
        this.setItemsVisibility(items, false);
      }
    }
  }

  private setItemsVisibility(items:Item[], flag){
    items.forEach((_item:Item)=>{
      _item.setVisibilityComponent(this.constructor.name, flag);
      _item.setSidebarItemVisibility(flag);
    })
  }


  private initChildListeners(isIgnoreFill?) {
    this.isChildInit$.next(true);
    let inputVal: itemSetUpInput = this.item.config.getInputProp(Item.SET_UP_ITEM_INPUT, 'value');
    this.parent = this.im.getItemById(inputVal.roomLayoutId);

    //remove item set up if item and parent room layout in other concrete layers
    if (this.item.config.getConcreteLayerId() != this.parent.config.getConcreteLayerId()){
      this.item.config.removeInput(Item.SET_UP_ITEM_INPUT);
      this.removeItemFromRoomLayout(inputVal.roomLayoutId, this.item);
      return;
    }

    if (!isIgnoreFill) {
      this.parent.getObservable('addItemToRoomLayout').next(this.item);
    }
  }

  private addItemToRoomLayout(selectedSetUpId: number | string, _item: Item) {

    if (!this.roomLayoutItems[selectedSetUpId]) {
      this.roomLayoutItems[selectedSetUpId] = [];
    }
    if (!this.roomLayoutItems[selectedSetUpId].some((i: Item) => i.getId() === _item.getId())) {
      this.roomLayoutItems[selectedSetUpId].push(_item);
    }
  }

  private removeItemFromRoomLayout(selectedSetUpId: number | string, _item: Item) {
    let index = -1;
    if (this.roomLayoutItems[selectedSetUpId].some((i: Item, _index) => {
      if (i.getId() === _item.getId()) {
        index = _index;
        return true;
      }
      return false;
    })) {
      if (index >= 0) {
        this.roomLayoutItems[selectedSetUpId].splice(index, 1);
      }
    }
    console.log('removeItemFromRoomLayout', this.roomLayoutItems);
  }

  private processBindingToRoomLayout(_item) {
    if (this.item.config.getConcreteLayerId() != _item.config.getConcreteLayerId()){
      return;
    }
    let _position = _item.config.getObjectConfig().getPosition() ? _item.config.getObjectConfig().getPosition() : _item.config.getObjectConfig().getCenterPosition();
    let point = turf.point([_position.lng, _position.lat]);
    let polygonArrays = this.item.config.getObjectConfig().getPositions().map(pos => pos.asSimpleArray());
    polygonArrays.push(polygonArrays[0]);
    let polygon = turf.polygon([polygonArrays]);
    let isIntersects = turf.booleanPointInPolygon(point, polygon);
    //add input to child item and push it to parent room layouts config if intersects
      let selectedSetUpId = this.item.config.getInputProp(Item.SET_UP_AREA_INPUT, 'selected');
    if (isIntersects) {
      _item.config.setInputValue(Item.SET_UP_ITEM_INPUT, 'value', {
        roomLayoutId: this.item.getId(),
        setUpId: selectedSetUpId,
      } as itemSetUpInput);
      if (Object.keys(this.roomLayoutItems).length == 0) {
        alert('Any objects can be placed within a room set-up, but only objects that have a 3D version will be shown on Venue Twin.');
      }
      this.addItemToRoomLayout(selectedSetUpId, _item);
      _item.getObservable('INIT_SET_UP_ITEM').next(true);
    }

    let inputVal:itemSetUpInput = _item.config.getInputProp(Item.SET_UP_ITEM_INPUT, 'value');
    if (!isIntersects && inputVal && inputVal.roomLayoutId == this.item.getId()) {
      _item.config.removeInput(Item.SET_UP_ITEM_INPUT);
      this.removeItemFromRoomLayout(selectedSetUpId, _item);
    }
  }
}
